import { useState } from 'react';
import { useWizardStore } from '../store/wizardStore';
import { StepShell } from '../components/StepShell';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { ApiError } from '../api/errors';
import {
  apiGenerateContract,
  apiResendContractEmail,
  pdfBase64ToBlob,
  type ContractPdfFile,
} from '../api/contract.api';
import { formatGateTotal } from '../utils/gateAmounts';
import { signingPersonLabel } from '../utils/signingPersons';
import { gateSigningPersonLabel } from '../utils/gateSigningPersons';

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function isResendableFile(f: {
  fileName: string;
  pdfBase64: string;
  kind?: ContractPdfFile['kind'];
}): f is ContractPdfFile {
  return Boolean(f.kind && f.pdfBase64 && f.fileName);
}

function normalizeGeneratedFiles(
  generated: Awaited<ReturnType<typeof apiGenerateContract>>,
): ContractPdfFile[] {
  const raw =
    generated.files.length > 0
      ? generated.files
      : [{ fileName: generated.fileName, pdfBase64: generated.pdfBase64 }];
  const out: ContractPdfFile[] = [];
  for (const f of raw) {
    if (isResendableFile(f)) out.push(f);
  }
  return out;
}

type EmailModalState =
  | { open: false }
  | { open: true; tone: 'success'; email: string }
  | { open: true; tone: 'error'; message: string; code?: string };

type DownloadablePdf = { fileName: string; pdfBase64: string };

export function Step6_Summary() {
  const state = useWizardStore();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailOk, setEmailOk] = useState(false);
  const [savedFiles, setSavedFiles] = useState<ContractPdfFile[] | null>(null);
  const [downloadableFiles, setDownloadableFiles] = useState<DownloadablePdf[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [emailModal, setEmailModal] = useState<EmailModalState>({ open: false });

  const hasInserts = state.inserts.length > 0;
  const canResendEmail = savedFiles !== null && savedFiles.length > 0 && !emailOk;
  const canDownloadPdf = downloadableFiles.length > 0;

  const contractNumber =
    state.variant === 'variant3'
      ? state.gate.contractNumber
      : state.windows.contractNumber;
  const contractDate =
    state.variant === 'variant3' ? state.gate.contractDate : state.windows.contractDate;
  const datePart = contractDate.split('-').reverse().join('.');

  const downloadPdfs = (): void => {
    for (const file of downloadableFiles) {
      triggerBrowserDownload(
        pdfBase64ToBlob(file.pdfBase64),
        file.fileName || `договор_${datePart}_${contractNumber}.pdf`,
      );
    }
  };

  const applyEmailResult = (
    emailSent: boolean,
    emailError?: { message: string; code?: string },
  ): void => {
    if (emailSent) {
      setEmailOk(true);
      setEmailModal({ open: true, tone: 'success', email: state.client.email });
      return;
    }
    setEmailModal({
      open: true,
      tone: 'error',
      message: emailError?.message ?? 'Не удалось отправить письмо',
      code: emailError?.code,
    });
  };

  const retryEmail = async (): Promise<void> => {
    if (!savedFiles?.length) return;

    setBusy(true);
    setErr(null);

    try {
      const result = await apiResendContractEmail(state, savedFiles);
      applyEmailResult(result.emailSent, result.emailError);
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : new ApiError('Не удалось отправить письмо');
      setEmailModal({
        open: true,
        tone: 'error',
        message: apiErr.message,
        code: apiErr.code,
      });
    } finally {
      setBusy(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (!hasInserts) {
      setErr('Прикрепите хотя бы один файл (расчёт/спецификацию) на шаге «Вставка»');
      return;
    }

    setBusy(true);
    setErr(null);
    setEmailModal({ open: false });
    setEmailOk(false);
    setSavedFiles(null);
    setDownloadableFiles([]);

    try {
      const generated = await apiGenerateContract(state);
      const filesForResend = normalizeGeneratedFiles(generated);
      const downloadFiles: DownloadablePdf[] =
        generated.files.length > 0
          ? generated.files.map((f) => ({ fileName: f.fileName, pdfBase64: f.pdfBase64 }))
          : [{ fileName: generated.fileName, pdfBase64: generated.pdfBase64 }];

      setDownloadableFiles(downloadFiles);
      setSavedFiles(filesForResend.length > 0 ? filesForResend : null);
      setSent(true);
      setEmailOk(generated.emailSent);
      applyEmailResult(generated.emailSent, generated.emailError);
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : new ApiError('Не удалось сформировать договор');
      setErr(apiErr.message);
      if (apiErr.code) {
        setSent(true);
        setEmailModal({
          open: true,
          tone: 'error',
          message: apiErr.message,
          code: apiErr.code,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const closeEmailModal = (): void => {
    setEmailModal({ open: false });
  };

  const downloadLabel = downloadableFiles.length > 1 ? 'Скачать договора' : 'Скачать договор';

  const resendFooter = (
    <>
      {canDownloadPdf && !emailOk && (
        <Button onClick={downloadPdfs}>
          {downloadLabel}
        </Button>
      )}
      {canResendEmail && (
        <Button variant="secondary" onClick={() => void retryEmail()} disabled={busy}>
          {busy ? 'Отправка…' : 'Попробовать ещё раз'}
        </Button>
      )}
      <Button variant="secondary" onClick={closeEmailModal}>
        {emailOk ? 'Понятно' : 'Закрыть'}
      </Button>
    </>
  );

  const gateTotalLabel = state.variant === 'variant3' ? formatGateTotal(state.gate) : '';

  if (sent) {
    return (
      <>
        <StepShell title="Готово" current="summary">
          <div className="bg-white rounded-2xl border p-8 max-w-xl mx-auto text-center">
            <div className="text-5xl mb-3">✓</div>
            <h2 className="text-xl font-semibold mb-2">PDF сформирован</h2>
            <p className="text-slate-400 text-sm mb-4">
              E-mail клиента: {state.client.email}
            </p>
            {emailOk ? (
              <p className="text-emerald-600 text-sm mb-4">Письмо отправлено на e-mail клиента.</p>
            ) : (
              <div className="mb-4 space-y-3">
                <p className="text-amber-700 text-sm">
                  Письмо не отправлено — можно повторить отправку или скачать PDF.
                </p>
                {canResendEmail && (
                  <Button onClick={() => void retryEmail()} disabled={busy}>
                    {busy ? 'Отправка…' : 'Попробовать отправить снова'}
                  </Button>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
              {canDownloadPdf && (
                <Button onClick={downloadPdfs}>
                  {state.variant === 'variant2' ? 'Скачать PDF (2 файла)' : 'Скачать PDF'}
                </Button>
              )}
              <Button variant="secondary" onClick={() => state.reset()}>
                Новая сделка
              </Button>
            </div>
          </div>
        </StepShell>

        {emailModal.open && emailModal.tone === 'success' && (
          <Modal
            open
            tone="success"
            title="Письмо отправлено"
            onClose={closeEmailModal}
            footer={resendFooter}
          >
            <p>
              {state.variant === 'variant2'
                ? 'Электронные версии договоров успешно отправлены на '
                : 'Электронная версия договора успешно отправлена на '}
              <span className="font-medium text-slate-900">{emailModal.email}</span>.
            </p>
          </Modal>
        )}

        {emailModal.open && emailModal.tone === 'error' && (
          <Modal
            open
            tone="error"
            title="Ошибка отправки письма"
            onClose={closeEmailModal}
            footer={resendFooter}
          >
            <p className="mb-2">{emailModal.message}</p>
            {emailModal.code && (
              <p className="font-mono text-xs bg-red-50 text-red-800 rounded px-2 py-1 inline-block">
                Код: {emailModal.code}
              </p>
            )}
            <p className="mt-3 text-slate-500">
              Скачайте PDF кнопкой на экране «Готово» или нажмите «Попробовать ещё раз».
            </p>
          </Modal>
        )}
      </>
    );
  }

  return (
    <StepShell
      title="Проверка и отправка"
      subtitle="Убедитесь, что данные корректны и приложения прикреплены"
      current="summary"
      footer={
        <div className="flex justify-between">
          <Button variant="secondary" onClick={() => state.goTo('insert')}>Назад</Button>
          <Button onClick={() => void submit()} disabled={busy || !hasInserts}>
            {busy ? 'Отправка…' : 'Сформировать и отправить'}
          </Button>
        </div>
      }
    >
      <div className="bg-white rounded-2xl border p-6 space-y-3 max-w-3xl">
        <Row label="Вариант" value={state.variant === 'variant2' ? 'Окна раздельный' : state.variant === 'variant3' ? 'Ворота' : 'Окна объединённый'} />
        <Row label="Клиент" value={state.client.fullName} />
        <Row label="E-mail" value={state.client.email} />
        <Row label="Телефон" value={state.client.phone} />
        {state.variant !== 'variant3' && (
          <>
            <Row label="Представитель" value={signingPersonLabel(state.signingPersonId)} />
            {state.variant === 'variant2' && state.signingPersonId === 'office' && (
              <>
                <Row label="Подписант ДКП" value="Коновалова Е.Ю." />
                <Row label="Подписант подряда" value="Федина Е.И." />
              </>
            )}
            <Row label="Номер договора окон" value={state.windows.contractNumber} />
            <Row label="Общая сумма окон" value={`${state.windows.totalAmount} руб.`} />
            <Row label="Адрес установки" value={state.windows.installationAddress} />
          </>
        )}
        {state.variant === 'variant2' && (
          <>
            <Row label="Договор подряда" value={`№${state.windows.contractNumber}/М`} />
          </>
        )}
        {state.variant === 'variant3' && (
          <>
            <Row label="Представитель" value={gateSigningPersonLabel(state.gateSigningPersonId)} />
            <Row label="Номер договора" value={state.gate.contractNumber} />
            <Row label="Сумма" value={gateTotalLabel ? `${gateTotalLabel} руб.` : '—'} />
            <Row label="Адрес установки" value={state.gate.installationAddress} />
          </>
        )}
        <Row
          label="Вставка"
          value={
            hasInserts
              ? state.inserts.map((f) => f.name).join(', ')
              : '— не прикреплена (обязательно)'
          }
        />
      </div>
      {!hasInserts && (
        <p className="text-amber-600 mt-4 text-sm">
          Вернитесь на шаг «Вставка» и прикрепите расчёт или спецификацию.
        </p>
      )}
      {err && <p className="text-red-600 mt-4">{err}</p>}
    </StepShell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5 gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-right">{value || '—'}</span>
    </div>
  );
}
